import React, {memo} from "react";
import PropTypes from "prop-types";
import {Box, ListItem, ListItemText} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {CancelButton, SubmitButton} from "../buttons";
import {formatResponse} from "@breachrx/streaming";
import {EDITOR_DOMPURIFY_CONFIG} from "../editor/EditorSettings";
import ChatbotCollapsible from "./ChatbotCollapsible";
import ChatbotMessageToolbar from "./ChatbotMessageToolbar";
import ConfirmationItemList from "./ConfirmationItemList";
import MessageAttachmentChip from "../files/MessageAttachmentChip";
import GeneratedDocumentChip from "../files/GeneratedDocumentChip";
import {useOrgSettings} from "../../context/OrgSettingsProvider";
import {useChatbotMessageStyles} from "./useChatbotMessageStyles";
import {cleanOperationResultForDisplay} from "./chatbotHelpers";
import PlaybookSelectorWidget from "./widgets/PlaybookSelectorWidget";
import {PLAYBOOK_WIDGET_TOOL_NAME} from "../agent/useAgentListTools/toolNames";
import pluralize from "pluralize";
import {isEmpty, map} from "underscore";

const ChatbotMessage = ({
  message,
  isStreaming,
  pendingConfirmation,
  onConfirmation,
  onDownloadDocument,
  attachments,
}) => {
  const {classes, cx} = useChatbotMessageStyles();
  const theme = useTheme();
  const {responseWorkflowName} = useOrgSettings();
  const isDarkTheme = theme.palette.mode === "dark";
  const isHuman = message.sender === "user";
  const messageText = message.text || "";
  const showPlaybookWidget = message.widgetType === PLAYBOOK_WIDGET_TOOL_NAME;
  const formattedMessage = formatResponse(
    messageText,
    isDarkTheme,
    isStreaming,
    EDITOR_DOMPURIFY_CONFIG,
  );
  const confirmationItems = pendingConfirmation?.confirmation?.items || [];
  const agentErrors = pendingConfirmation?.agentErrors || message.agentErrors;
  const confirmationResultItems = message.confirmationResult?.items || [];
  const isCancelled = confirmationResultItems?.[0]?.status === "denied";
  const confirmationResultTitle = isCancelled ?
    `${pluralize("operation", confirmationResultItems.length, true)} cancelled` :
    `${pluralize("operation", confirmationResultItems.length, true)} approved` +
    `${isStreaming ? " — executing..." : ""}`;

  return (
    <ListItem
      alignItems="flex-start"
      className={cx(classes.listItem, isHuman ? classes.listItemHuman : classes.listItemBot)}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems={isHuman ? "flex-end" : "flex-start"}
        width="100%"
      >
        <Box
          className={cx(
            classes.messageBox,
            isHuman ? classes.messageBoxHuman : classes.messageBoxBot,
          )}
        >
          {!isHuman && message.thinking && (
            <ChatbotCollapsible title="Reasoning" expanded>
              <em>{message.thinking}</em>
            </ChatbotCollapsible>
          )}
          {showPlaybookWidget ?
            (
              <PlaybookSelectorWidget
                disabled
                value={message.widgetValue}
                widgetLabel={message.widgetLabel}
              />
            ) :
            (
              <ListItemText
                primary={<span dangerouslySetInnerHTML={{__html: formattedMessage}} />}
                primaryTypographyProps={{
                  className: isHuman ? classes.textHuman : classes.textBot,
                }}
              />
            )}
          {!isHuman && message.activity && (
            <Box className={classes.activityMessage} role="status" aria-live="polite">
              {message.activity.message}
            </Box>
          )}
          {!isEmpty(attachments) &&
            map(attachments, (attachment) => (
              <MessageAttachmentChip
                key={attachment.docId || attachment.fileName}
                fileName={attachment.fileName}
                id={attachment.docId}
              />
            ))}
          {!isEmpty(message.generatedDocuments) &&
            map(message.generatedDocuments, (doc) => (
              <GeneratedDocumentChip
                key={doc.docId}
                fileName={doc.fileName}
                fileSize={doc.fileSize}
                onDownload={() => onDownloadDocument?.(doc.docId, doc.fileName)}
              />
            ))}
          {confirmationResultItems.length > 0 && (
            <ChatbotCollapsible title={confirmationResultTitle}>
              <ConfirmationItemList
                items={confirmationResultItems}
                responseWorkflowName={responseWorkflowName}
                showStatus
              />
            </ChatbotCollapsible>
          )}
          {message.agentResult?.operations?.length > 0 && (
            <ChatbotCollapsible title="View Operation Details">
              <ConfirmationItemList
                items={map(message.agentResult.operations, (op) => ({
                  input: {
                    tool: op.tool?.replace(/^mcp_/, "").replace(/_/g, "."),
                    status: op.status,
                    result: cleanOperationResultForDisplay(op.result),
                  },
                }))}
                responseWorkflowName={responseWorkflowName}
              />
            </ChatbotCollapsible>
          )}
          {agentErrors?.length > 0 && (
            <ChatbotCollapsible
              title={`⚠️ ${pluralize("operation", agentErrors.length, true)} failed`}
            >
              {JSON.stringify(
                map(agentErrors, (err) => ({
                  tool: err.tool?.replace(/^mcp_/, "").replace(/_/g, "."),
                  error: err.error || err.result?.error || "Unknown error",
                })),
                null,
                2,
              )}
            </ChatbotCollapsible>
          )}
          {confirmationItems.length > 0 && (
            <Box sx={{mt: 1}}>
              <ChatbotCollapsible
                title={`${pluralize("operation", confirmationItems.length, true)} pending approval`}
                expanded
              >
                <ConfirmationItemList
                  items={confirmationItems}
                  responseWorkflowName={responseWorkflowName}
                  showStatus={false}
                />
              </ChatbotCollapsible>
              <Box className={classes.confirmationButtons}>
                <CancelButton onCancel={() => onConfirmation(false)} variant="inline" />
                <SubmitButton onSubmit={() => onConfirmation(true)} isSubmit={false} inline>
                  {confirmationItems.length === 1 ? "Approve" : "Approve All"}
                </SubmitButton>
              </Box>
            </Box>
          )}
          {message.aborted && (
            <Box className={classes.abortedMessage}>[Response stopped by user]</Box>
          )}
        </Box>
        <ChatbotMessageToolbar
          message={message}
          hide={isHuman || isStreaming || !messageText || message.ephemeral}
        />
      </Box>
    </ListItem>
  );
};

ChatbotMessage.propTypes = {
  message: PropTypes.object.isRequired,
  isStreaming: PropTypes.bool,
  pendingConfirmation: PropTypes.object,
  onConfirmation: PropTypes.func,
  onDownloadDocument: PropTypes.func,
  attachments: PropTypes.array,
};

export default memo(ChatbotMessage);
